import useSWRInfinite from "swr/infinite"

import {
    APPLICATION_PAGE_SIZE,
    FIRST_PAGE,
} from "../../../../constants/generalConstants"
import { invalidApiDataError } from "../../../../errors/recognisedErrors"
import { postClientProtected } from "../../clients/clients"
import { installerSingleApplicationsMapper } from "../../mappers/installerInterface/installerApplicationsMapper"
import { APPLICATION_LIST_CONFIG } from "../../swrConfig"
import {
    IApplicationListResponse,
    IListApplications,
    TGetInstallerApplications,
} from "../../types/installerInterface/installerApplicationTypes"
import { INSTALLER_LIST_APPLICATIONS } from "../../urls/hybrid"

/**
 * Fetches installer applications using SWR (React Hooks for data fetching).
 * @param {URLSearchParams} searchParams - The search parameters for the API request.
 * @returns {TGetInstallerApplications} - The result of the API request.
 */
const useGetInstallerApplications = (
    searchParams: URLSearchParams,
    sortKey: string
): TGetInstallerApplications => {
    const searchParamsLocal = new URLSearchParams(searchParams.toString())

    // interface for ouput of getKey function
    type TGetKey = {
        url: string
        nextPageKey: string | Record<string, string> | null
        pageIndex: number
    }

    /**
     * Injects key into useSWRInfinite hook. Key is an object with API URL
     * and pagination body.
     * @param {number} pageIndex - page number
     * @param {IApplicationListResponse} previousPageData - previous page data
     * @returns {TGetKey | null } - object with API URL and pagination body
     */
    const getKey = (
        pageIndex: number,
        previousPageData: IApplicationListResponse
    ): TGetKey | null => {
        const url = INSTALLER_LIST_APPLICATIONS(
            searchParamsLocal.toString(),
            APPLICATION_PAGE_SIZE,
            sortKey
        )

        if (!previousPageData)
            return { url, pageIndex, nextPageKey: FIRST_PAGE }
        if (previousPageData && !previousPageData.nextPageKey) return null // reached the end
        return { url, pageIndex, nextPageKey: previousPageData.nextPageKey }
    }

    const {
        data: pageResults,
        error,
        isLoading,
        isValidating,
        size,
        setSize,
    } = useSWRInfinite(
        getKey,
        async (key: TGetKey): Promise<IApplicationListResponse> => {
            try {
                // Extract pagination body
                let body
                if (typeof key?.nextPageKey === "string") body = null
                else body = key?.nextPageKey

                const res = await postClientProtected(key?.url, body)
                return installerSingleApplicationsMapper.mapList(res)
            } catch {
                throw invalidApiDataError(
                    "An error occurred while fetching the installer application."
                )
            }
        },
        APPLICATION_LIST_CONFIG
    )
    // return true if any item in allPageResults has a null value for nextPageKey
    const isFinalPage = pageResults
        ? pageResults.some((page) => !page.nextPageKey)
        : false

    // Merge new data into existing paginated data
    const allPageResults = pageResults
        ? pageResults?.reduce(
              (
                  acc: IListApplications,
                  pageResult: IApplicationListResponse
              ) => [...acc, ...pageResult.data],
              []
          )
        : undefined

    return {
        data: allPageResults,
        isLoading,
        isError: error,
        isValidating,
        page: size,
        isFinalPage,
        setPage: setSize,
    }
}

export default useGetInstallerApplications
